import { BehaviorSubject, Observable } from 'rxjs';
import {
  UserInterfaceStateService,
  UserInterfaceStateServiceLocation,
} from './user-interface-state.service';

import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export enum ThemeMode {
  Light = 'light',
  Dark = 'dark',
}

export enum ThemeSkin {
  Default,
  Alternative,
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private root: Element | null;

  private theme = ThemeMode.Light;
  private userTheme = ThemeMode.Light;
  private themeMode = ThemeMode;
  private isThemeAuto = false;
  private themeSubject = new BehaviorSubject<ThemeMode>(ThemeMode.Light);
  private defaultTheme: ThemeMode;

  // Expose only the Observable part of the BehaviorSubject to prevent clients from pushing new values
  public theme$: Observable<ThemeMode> = this.themeSubject;

  constructor(
    private userInterfaceStateService: UserInterfaceStateService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  public InitThemeService(defaultTheme: ThemeMode = ThemeMode.Light): Promise<void> {
    this.defaultTheme = defaultTheme;

    return new Promise((resolve) => {
      try {
        this.root = document.querySelector(':root');

        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (_event) => {
          this.detectTheme();
        });

        if (
          this.userInterfaceStateService.getValue('userTheme') === this.themeMode.Dark.toString()
        ) {
          this.setTheme(this.themeMode.Dark);
        } else {
          this.setTheme(this.themeMode.Light);
        }
      } catch {
        this.applyThemeFromStorage();
      }
      resolve();
    });
  }

  private detectTheme() {
    this.isThemeAuto = this.userInterfaceStateService.getValue('isThemeAuto') === 'true';

    // local storage is used to override OS theme settings
    if (this.isThemeAuto) {
      if (!window.matchMedia) {
        // matchMedia method not supported
        return false;
      } else {
        if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
          // OS theme setting detected as dark
          this.applyTheme(this.themeMode.Dark);
        } else {
          this.applyTheme(this.themeMode.Light);
        }
      }
      return;
    }

    this.applyThemeFromStorage();
  }

  private applyThemeFromStorage() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.userInterfaceStateService.getValue('userTheme')) {
        if (
          this.userInterfaceStateService.getValue('userTheme') === this.themeMode.Dark.toString()
        ) {
          this.userTheme = this.themeMode.Dark;
          this.applyTheme(this.themeMode.Dark);
        } else {
          this.userTheme = this.themeMode.Light;
          this.applyTheme(this.themeMode.Light);
        }
      } else {
        this.setTheme(this.defaultTheme);
      }
    }
  }

  private applyTheme(theme: ThemeMode) {
    if (theme === ThemeMode.Dark) {
      this.root?.setAttribute('data-theme', 'dark');
    } else {
      this.root?.setAttribute('data-theme', 'light');
    }

    this.theme = theme;
    this.userInterfaceStateService.setValue(
      'userTheme',
      theme.toString(),
      UserInterfaceStateServiceLocation.LocalStorage,
    );
    this.themeSubject.next(theme);
  }

  public getAutoMode() {
    return this.isThemeAuto;
  }
  public getCurrentTheme() {
    return this.theme;
  }
  public getUserTheme() {
    return this.userTheme;
  }
  public setAutoMode(isAutoMode: boolean) {
    this.isThemeAuto = isAutoMode;
    this.userInterfaceStateService.setValue(
      'isThemeAuto',
      this.isThemeAuto + '',
      UserInterfaceStateServiceLocation.LocalStorage,
    );
    this.detectTheme();
  }

  public setTheme(theme: ThemeMode) {
    this.userTheme = theme;
    this.userInterfaceStateService.setValue(
      'userTheme',
      theme.toString(),
      UserInterfaceStateServiceLocation.LocalStorage,
    );
    this.detectTheme();
  }
}
